import React from 'react';

import { FileText, Pencil, Percent } from 'lucide-react';

export type ViewMode = 'default' | 'user_edits' | 'customized_quote';

interface ViewSwitcherProps {
	currentView: ViewMode;
	onViewChange: (view: ViewMode) => void;
	hasUserEdits: boolean;
	hasCustomizedQuote: boolean;
}

const ViewSwitcher: React.FC<ViewSwitcherProps> = ({
	currentView,
	onViewChange,
	hasUserEdits,
	hasCustomizedQuote,
}) => {
	const views = [
		{
			id: 'default' as ViewMode,
			label: 'Original',
			icon: FileText,
			available: true,
		},
		{
			id: 'user_edits' as ViewMode,
			label: 'With Edits',
			icon: Pencil,
			available: hasUserEdits,
		},
		{
			id: 'customized_quote' as ViewMode,
			label: 'Customized',
			icon: Percent,
			available: hasCustomizedQuote,
		},
	];

	const availableViews = views.filter((v) => v.available);

	// If only one view is available, don't show switcher
	if (availableViews.length <= 1) {
		return null;
	}

	return (
		<div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
			{availableViews.map((view) => {
				const Icon = view.icon;
				const isActive = currentView === view.id;

				return (
					<button
						key={view.id}
						onClick={() => onViewChange(view.id)}
						className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
							isActive
								? 'bg-white text-indigo-600 shadow-sm'
								: 'text-gray-600 hover:text-gray-900'
						}`}
						title={`Switch to ${view.label} view`}
						aria-label={`Switch to ${view.label} view`}
						aria-pressed={isActive}
					>
						<Icon className="w-4 h-4" />
						{view.label}
					</button>
				);
			})}
		</div>
	);
};

export default ViewSwitcher;
